import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
    Target, Download, CheckCircle, Send, Eye, 
    ChevronDown, ChevronUp, Loader2, TrendingUp, TrendingDown, Minus
} from 'lucide-react';
import { formatDate } from '../../lib/dateUtils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const stateLabels = { generated: 'Generado', under_review: 'En revisión', approved: 'Aprobado', delivered: 'Entregado' };
const stateColors = { generated: 'bg-[#9AA5B1]/10 text-[#9AA5B1]', under_review: 'bg-[#E8B27A]/10 text-[#E8B27A]', approved: 'bg-[#5FAF9D]/10 text-[#5FAF9D]', delivered: 'bg-[#3E5F6E]/10 text-[#3E5F6E]' };

export default function MentorPlan3x3Tab() {
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedPlan, setExpandedPlan] = useState(null);
    const [planDetail, setPlanDetail] = useState(null);
    const [scoreData, setScoreData] = useState(null);
    const [comments, setComments] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchPlans = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API_URL}/api/mentor/plans3x3`, { withCredentials: true });
            setPlans(data.plans || []);
        } catch {
            toast.error('Error al cargar planes 3x3');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchPlans(); }, [fetchPlans]);

    const handleExpand = async (plan) => {
        if (expandedPlan === plan.user_id) {
            setExpandedPlan(null);
            setPlanDetail(null);
            setScoreData(null);
            return;
        }
        setExpandedPlan(plan.user_id);
        try {
            const [detailRes, scoreRes] = await Promise.all([
                axios.get(`${API_URL}/api/mentor/plan3x3/${plan.user_id}`, { withCredentials: true }),
                axios.get(`${API_URL}/api/mentor/plan3x3/${plan.user_id}/execution-score`, { withCredentials: true })
            ]);
            setPlanDetail(detailRes.data.plan);
            setScoreData(scoreRes.data.score_data);
            setComments(detailRes.data.plan?.mentor_comments || '');
        } catch {
            toast.error('Error al cargar detalle del plan');
        }
    };

    const handleSaveComments = async (userId) => {
        setSaving(true);
        try {
            await axios.post(`${API_URL}/api/mentor/plan3x3/${userId}/comment`, { comments }, { withCredentials: true });
            toast.success('Comentarios guardados');
        } catch {
            toast.error('Error al guardar comentarios');
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (userId, deliver = false) => {
        try {
            await axios.post(`${API_URL}/api/mentor/plan3x3/${userId}/approve`, { deliver, edits: null }, { withCredentials: true });
            toast.success(deliver ? 'Plan aprobado y entregado' : 'Plan aprobado');
            fetchPlans();
            setExpandedPlan(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error');
        }
    };

    const handleDownloadPDF = async (userId, userName) => {
        try {
            const response = await axios.get(`${API_URL}/api/mentor/plan3x3/${userId}/pdf`, { withCredentials: true, responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `Plan_SENDA_3x3_${userName.replace(/\s+/g, '_')}.pdf`;
            link.click();
            window.URL.revokeObjectURL(url);
        } catch {
            toast.error('Error al descargar PDF');
        }
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-[#3E5F6E]" /></div>;

    if (plans.length === 0) {
        return (
            <Card className="bg-white border-[#EAE8E3]">
                <CardContent className="py-12 text-center">
                    <Target className="h-10 w-10 text-[#9AA5B1] mx-auto mb-3" />
                    <p className="text-sm text-[#616E7C]">Aún no hay planes SENDA 3x3 enviados para revisión.</p>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-4" data-testid="mentor-plan3x3-tab">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-[#1F2933] font-['Outfit']">Planes SENDA 3x3</h2>
                <Badge className="bg-[#3E5F6E]/10 text-[#3E5F6E]">{plans.length} planes</Badge>
            </div>

            {plans.map(plan => (
                <Card key={plan.user_id} className="bg-white border-[#EAE8E3] overflow-hidden">
                    <CardContent className="p-0">
                        {/* Header row */}
                        <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-[#FBFBF9] transition-colors" onClick={() => handleExpand(plan)} data-testid={`plan-row-${plan.user_id}`}>
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 bg-[#3E5F6E]/10 rounded-lg flex items-center justify-center shrink-0">
                                    <Target className="h-4 w-4 text-[#3E5F6E]" />
                                </div>
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-[#1F2933] truncate">{plan.user_name || 'Usuario'}</p>
                                    <p className="text-xs text-[#9AA5B1]">{plan.user_email} | v{plan.version} | {formatDate(plan.created_at)}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                                <Badge className={`text-xs ${stateColors[plan.state]}`}>{stateLabels[plan.state]}</Badge>
                                {expandedPlan === plan.user_id ? <ChevronUp className="h-4 w-4 text-[#9AA5B1]" /> : <ChevronDown className="h-4 w-4 text-[#9AA5B1]" />}
                            </div>
                        </div>

                        {/* Expanded detail */}
                        {expandedPlan === plan.user_id && planDetail && (
                            <div className="border-t border-[#EAE8E3] p-4 space-y-4 bg-[#FBFBF9]">
                                {/* Execution Score */}
                                {scoreData && scoreData.score > 0 && (
                                    <div className="p-3 bg-white rounded-xl border border-[#EAE8E3]">
                                        <div className="flex items-center justify-between mb-2">
                                            <p className="text-xs font-semibold text-[#3E5F6E] uppercase">Execution Score</p>
                                            <div className="flex items-center gap-1">
                                                {scoreData.trend === 'up' && <TrendingUp className="h-3 w-3 text-[#5FAF9D]" />}
                                                {scoreData.trend === 'down' && <TrendingDown className="h-3 w-3 text-[#D07F7E]" />}
                                                {scoreData.trend === 'stable' && <Minus className="h-3 w-3 text-[#9AA5B1]" />}
                                                <span className="text-lg font-bold text-[#1F2933]">{scoreData.score}/100</span>
                                            </div>
                                        </div>
                                        <p className="text-xs text-[#616E7C] italic">{scoreData.interpretation}</p>
                                        <p className="text-[10px] text-[#9AA5B1] mt-1">Semanas registradas: {scoreData.weeks_tracked}</p>
                                    </div>
                                )}

                                {/* Plan Summary */}
                                <div className="p-3 bg-white rounded-xl border border-[#EAE8E3]">
                                    <p className="text-xs font-semibold text-[#425A65] uppercase mb-2">Resumen del plan</p>
                                    {planDetail.generated_plan?.diagnostico_estrategico?.problema_real && (
                                        <p className="text-sm text-[#1F2933] italic mb-2">"{planDetail.generated_plan.diagnostico_estrategico.problema_real}"</p>
                                    )}
                                    <div className="grid grid-cols-3 gap-2 mt-2">
                                        {(planDetail.generated_plan?.plan_ejecucion || []).map((proj, i) => (
                                            <div key={i} className="p-2 bg-[#FBFBF9] rounded-lg">
                                                <p className="text-[10px] text-[#9AA5B1]">Proyecto {i + 1}</p>
                                                <p className="text-xs font-medium text-[#1F2933] truncate">{proj.proyecto}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Checkins Summary */}
                                {planDetail.checkins && planDetail.checkins.length > 0 && (
                                    <div className="p-3 bg-white rounded-xl border border-[#EAE8E3]">
                                        <p className="text-xs font-semibold text-[#425A65] uppercase mb-2">Seguimientos ({planDetail.checkins.length})</p>
                                        {planDetail.checkins.slice(0, 3).map((c, i) => (
                                            <div key={i} className="p-2 bg-[#FBFBF9] rounded-lg mb-1 text-xs">
                                                <p className="text-[#9AA5B1]">{formatDate(c.created_at)}</p>
                                                <p className="text-[#323F4B]"><strong>Completado:</strong> {c.completed?.slice(0, 100)}</p>
                                                {c.avoided && <p className="text-[#D07F7E]"><strong>Evitado:</strong> {c.avoided?.slice(0, 80)}</p>}
                                                {c.emotion && <p className="text-[#A29DB0]"><strong>Emoción:</strong> {c.emotion}</p>}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Mentor Comments */}
                                <div className="space-y-2">
                                    <label className="text-xs font-medium text-[#425A65]">Comentarios para el usuario</label>
                                    <Textarea value={comments} onChange={e => setComments(e.target.value)} rows={3} placeholder="Observaciones, recomendaciones o ajustes..." data-testid="mentor-plan-comments" />
                                    <Button size="sm" variant="outline" onClick={() => handleSaveComments(plan.user_id)} disabled={saving} data-testid="save-plan-comments-btn">
                                        {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                                        Guardar comentarios
                                    </Button>
                                </div>

                                {/* Actions */}
                                <div className="flex items-center gap-2 pt-2 border-t border-[#EAE8E3]">
                                    {plan.state === 'under_review' && (
                                        <>
                                            <Button size="sm" className="bg-[#5FAF9D] hover:bg-[#4A9A89] text-white" onClick={() => handleApprove(plan.user_id, false)} data-testid="approve-plan-btn">
                                                <CheckCircle className="h-3 w-3 mr-1" /> Aprobar
                                            </Button>
                                            <Button size="sm" className="bg-[#3E5F6E] hover:bg-[#2F4A57] text-white" onClick={() => handleApprove(plan.user_id, true)} data-testid="approve-deliver-plan-btn">
                                                <Send className="h-3 w-3 mr-1" /> Aprobar y entregar
                                            </Button>
                                        </>
                                    )}
                                    <Button size="sm" variant="outline" onClick={() => handleDownloadPDF(plan.user_id, plan.user_name)} data-testid="download-plan-pdf-mentor">
                                        <Download className="h-3 w-3 mr-1" /> PDF
                                    </Button>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            ))}
        </div>
    );
}
